import { useEffect, useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useCart } from '@/contexts/CartContext';
import { useUEat } from '@/contexts/UEatContext';
import { useOrdering } from '@/contexts/OrderingContext';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ShoppingCart, Plus, Minus, Star, Clock, Sliders, ExternalLink, Search } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function CustomizeDialog({ open, onOpenChange, item, lang, modifiers, qty, onConfirm }) {
  const [selected, setSelected] = useState({});

  useEffect(() => {
    if (open) setSelected({});
  }, [open]);

  if (!item) return null;
  const itemModifiers = modifiers.filter(m => m.available !== false && (m.categories || []).includes(item.category));
  const condiments = itemModifiers.filter(m => m.type === 'condiment');
  const extras = itemModifiers.filter(m => m.type === 'extra');

  const toggle = (m) => setSelected(s => ({ ...s, [m.id]: s[m.id] ? undefined : m }));
  const chosen = Object.values(selected).filter(Boolean);
  const extraTotal = chosen.reduce((sum, m) => sum + (m.price || 0), 0);

  const confirm = () => {
    onConfirm(chosen);
    onOpenChange(false);
  };

  const renderRow = (m, isExtra) => (
    <label key={m.id} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-[#e0e0e0] cursor-pointer hover:bg-[#f5f5f0]" data-testid={`modifier-${m.id}`}>
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <input
          type="checkbox"
          checked={!!selected[m.id]}
          onChange={() => toggle(m)}
          className="accent-[#e53a3a] w-4 h-4 shrink-0"
        />
        <span className="text-sm font-medium truncate">{lang === 'fr' ? m.name_fr : m.name_en}</span>
      </div>
      {isExtra && <span className="text-sm font-semibold text-[#e53a3a]">+{(m.price || 0).toFixed(2)} $</span>}
    </label>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="customize-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {lang === 'fr' ? 'Personnaliser' : 'Customize'} — {lang === 'fr' ? item.name_fr : item.name_en}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {condiments.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-[#1a1a1a] mb-2">
                {lang === 'fr' ? 'Condiments (inclus)' : 'Condiments (included)'}
              </h4>
              <div className="space-y-2">
                {condiments.map(m => renderRow(m, false))}
              </div>
            </div>
          )}
          {extras.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-[#1a1a1a] mb-2">
                {lang === 'fr' ? 'Extras (payants)' : 'Extras (paid)'}
              </h4>
              <div className="space-y-2">
                {extras.map(m => renderRow(m, true))}
              </div>
            </div>
          )}
          {condiments.length === 0 && extras.length === 0 && (
            <p className="text-sm text-[#555555] text-center py-4">
              {lang === 'fr' ? 'Aucune option disponible pour cet item.' : 'No options available for this item.'}
            </p>
          )}
          <div className="border-t pt-3 flex items-center justify-between">
            <div>
              <p className="text-xs text-[#555555]">{lang === 'fr' ? 'Total' : 'Total'}</p>
              <p className="text-lg font-bold text-[#e53a3a]">{((item.price + extraTotal) * qty).toFixed(2)} $</p>
            </div>
            <button
              onClick={confirm}
              className="btn-belgo px-5 py-2.5 rounded-lg text-white font-semibold"
              style={{ backgroundColor: '#e53a3a' }}
              data-testid="customize-confirm"
            >
              {lang === 'fr' ? 'Ajouter au panier' : 'Add to cart'}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MenuItemCard({ item, lang, onAdd, modifiers, ordering }) {
  const [qty, setQty] = useState(1);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const sizes = Array.isArray(item.options) ? item.options.filter(o => o && o.name_fr) : [];
  const [selectedSize, setSelectedSize] = useState(sizes[0] || null);
  const name = lang === 'fr' ? item.name_fr : item.name_en;
  const desc = lang === 'fr' ? item.description_fr : item.description_en;
  const effectivePrice = item.price + (selectedSize?.price_modifier || 0);

  const hasModifiers = modifiers.some(m => m.available !== false && (m.categories || []).includes(item.category));

  const quickAdd = () => {
    onAdd(item, qty, selectedSize, []);
    setQty(1);
    toast.success(lang === 'fr' ? 'Ajouté au panier!' : 'Added to cart!');
  };

  const onCustomize = (chosen) => {
    onAdd(item, qty, selectedSize, chosen);
    setQty(1);
    toast.success(lang === 'fr' ? 'Ajouté au panier!' : 'Added to cart!');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      className="card-hover bg-white rounded-xl border border-[#e0e0e0] overflow-hidden flex flex-col"
      data-testid={`menu-item-${item.id}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        {item.image_url ? (
          <img src={item.image_url} alt={name} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full bg-[#f5f5f0] flex items-center justify-center text-[#bbb] text-xs">{lang === 'fr' ? 'Pas d\'image' : 'No image'}</div>
        )}
        {item.featured && (
          <Badge className="absolute top-3 left-3 text-xs" style={{ backgroundColor: '#f4d00d', color: 'black' }}>
            <Star className="w-3 h-3 mr-1 fill-white" /> {lang === 'fr' ? 'Vedette' : 'Featured'}
          </Badge>
        )}
        {!item.available && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <span className="text-white font-semibold">{lang === 'fr' ? 'Non disponible' : 'Unavailable'}</span>
          </div>
        )}
      </div>
      <div className="p-5 flex flex-col flex-1">
        <h3 className="font-heading text-lg font-semibold text-[#1a1a1a] mb-1">{name}</h3>
        <p className="text-xs text-[#555555] mb-3 flex-1 line-clamp-2">{desc}</p>

        {/* Size selector — only shown if item has options/sizes */}
        {sizes.length > 0 && item.available && (
          <div className="mb-3" data-testid={`sizes-${item.id}`}>
            <p className="text-[10px] uppercase tracking-wider text-[#888] font-semibold mb-1.5">{lang === 'fr' ? 'Format' : 'Size'}</p>
            <div className="flex flex-wrap gap-1.5">
              {sizes.map((sz, idx) => {
                const isActive = selectedSize === sz;
                const mod = sz.price_modifier || 0;
                return (
                  <button
                    key={idx}
                    onClick={() => setSelectedSize(sz)}
                    className={`px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${
                      isActive
                        ? 'bg-[#e53a3a] text-white border-[#e53a3a]'
                        : 'bg-white text-[#1a1a1a] border-[#e0e0e0] hover:border-[#e53a3a]'
                    }`}
                    data-testid={`size-${item.id}-${idx}`}
                  >
                    {lang === 'fr' ? sz.name_fr : sz.name_en}
                    {mod !== 0 && <span className="ml-1 opacity-75">{mod > 0 ? `+${mod.toFixed(2)}` : mod.toFixed(2)}$</span>}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex items-center justify-between gap-2">
          <span className="text-lg font-bold text-[#e53a3a]" data-testid={`price-${item.id}`}>{effectivePrice.toFixed(2)} $</span>
          {item.available && ordering?.takeout_enabled !== false && (
            <div className="flex items-center gap-2">
              <div className="flex items-center border border-[#e0e0e0] rounded-lg">
                <button onClick={() => setQty(q => Math.max(1, q - 1))} className="p-1.5 hover:bg-[#f5f5f0] rounded-l-lg" data-testid={`qty-minus-${item.id}`}>
                  <Minus className="w-3 h-3" />
                </button>
                <span className="px-2 text-sm font-medium">{qty}</span>
                <button onClick={() => setQty(q => q + 1)} className="p-1.5 hover:bg-[#f5f5f0] rounded-r-lg" data-testid={`qty-plus-${item.id}`}>
                  <Plus className="w-3 h-3" />
                </button>
              </div>
              {hasModifiers ? (
                <button
                  onClick={() => setCustomizeOpen(true)}
                  className="btn-belgo px-3 py-2 rounded-lg text-white text-xs font-semibold flex items-center gap-1"
                  style={{ backgroundColor: '#e53a3a' }}
                  data-testid={`customize-${item.id}`}
                  title={lang === 'fr' ? 'Personnaliser' : 'Customize'}
                >
                  <Sliders className="w-3.5 h-3.5" />
                  {lang === 'fr' ? 'Choisir' : 'Pick'}
                </button>
              ) : (
                <button
                  onClick={quickAdd}
                  className="btn-belgo p-2.5 rounded-lg text-white"
                  style={{ backgroundColor: '#e53a3a' }}
                  data-testid={`add-to-cart-${item.id}`}
                >
                  <ShoppingCart className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      <CustomizeDialog
        open={customizeOpen}
        onOpenChange={setCustomizeOpen}
        item={item}
        lang={lang}
        modifiers={modifiers}
        qty={qty}
        onConfirm={onCustomize}
      />
    </motion.div>
  );
}

export default function MenuPage() {
  const { t, lang } = useLanguage();
  const { addItem } = useCart();
  const ueat = useUEat();
  const ordering = useOrdering();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([{ key: 'all', name_fr: 'Tout', name_en: 'All' }]);
  const [modifiers, setModifiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [isOpen, setIsOpen] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    axios.get(`${API}/menu/available`).then(r => {
      setItems(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
    axios.get(`${API}/categories`).then(r => {
      const all = { key: 'all', name_fr: 'Tout', name_en: 'All' };
      setCategories([all, ...r.data]);
    }).catch(() => {});
    axios.get(`${API}/modifiers`).then(r => setModifiers(r.data)).catch(() => {});
    axios.get(`${API}/business-hours`).then(r => {
      setIsOpen(r.data.is_open);
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const byCategory = activeTab === 'all' ? items : items.filter(i => i.category === activeTab);
  const q = search.trim().toLowerCase();
  const filtered = !q ? byCategory : byCategory.filter(i =>
    (i.name_fr || '').toLowerCase().includes(q) ||
    (i.name_en || '').toLowerCase().includes(q) ||
    (i.description_fr || '').toLowerCase().includes(q) ||
    (i.description_en || '').toLowerCase().includes(q)
  );

  return (
    <div className="pt-20 lg:pt-24 pb-20" data-testid="menu-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* UEat Banner */}
        {ueat.enabled && ueat.url && (
          <div className="mb-8 bg-[#e53a3a] text-white rounded-xl p-5 flex items-center gap-4" data-testid="ueat-banner">
            <ExternalLink className="w-8 h-8 text-[#f4d00d] shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-base">
                {lang === 'fr' ? ueat.banner_message_fr : ueat.banner_message_en}
              </p>
            </div>
            <a
              href={ueat.url}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-belgo shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-bold text-black bg-[#f4d00d] hover:bg-[#d4b800] transition-colors"
              data-testid="ueat-banner-cta"
            >
              {lang === 'fr' ? ueat.label_fr : ueat.label_en}
            </a>
          </div>
        )}

        {/* Ordering disabled banner */}
        {!ueat.enabled && ordering.takeout_enabled === false && (
          <div className="mb-8 bg-[#1a1a1a] text-white rounded-xl p-5 flex items-center gap-4" data-testid="takeout-disabled-banner">
            <Clock className="w-8 h-8 text-[#f4d00d] shrink-0" />
            <p className="font-semibold text-base">
              {lang === 'fr' ? (ordering.disabled_message_fr || 'Les commandes en ligne sont temporairement indisponibles.') : (ordering.disabled_message_en || 'Online ordering is temporarily unavailable.')}
            </p>
          </div>
        )}

        {/* Closed Banner */}
        {!isOpen && (
          <div className="mb-8 bg-black text-white rounded-xl p-5 flex items-center gap-4" data-testid="closed-banner">
            <Clock className="w-8 h-8 text-[#f4d00d] shrink-0" />
            <div>
              <p className="font-semibold text-lg">
                {lang === 'fr' ? 'Le restaurant est présentement fermé' : 'The restaurant is currently closed'}
              </p>
              <p className="text-sm text-gray-300">
                {lang === 'fr'
                  ? 'Les commandes pour emporter sont acceptées pendant les heures d\'ouverture seulement. Vous pouvez consulter le menu en attendant!'
                  : 'Takeout orders are accepted during business hours only. Feel free to browse the menu!'}
              </p>
              <p className="text-xs text-[#f4d00d] mt-1">Mer 11h-19h | Jeu 11h-21h | Ven 11h-22h | Sam 11h-21h | Dim 11h-20h</p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-12">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-heading text-4xl sm:text-5xl font-bold text-[#1a1a1a] tracking-tight"
          >
            {t('menu.title')}
          </motion.h1>
          <p className="text-[#555555] mt-3 text-base">{t('menu.subtitle')}</p>
          <div className="w-16 h-1 mx-auto mt-4 bg-[#e53a3a]" />
        </div>

        {/* Search bar */}
        <div className="max-w-xl mx-auto mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#888]" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={lang === 'fr' ? 'Rechercher un plat...' : 'Search for a dish...'}
              className="w-full pl-10 pr-10 py-3 rounded-lg border border-[#e0e0e0] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#e53a3a]"
              data-testid="menu-search-input"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#888] hover:text-[#e53a3a]" data-testid="menu-search-clear">
                <Plus className="w-4 h-4 rotate-45" />
              </button>
            )}
          </div>
        </div>

        {/* Category Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-12">
          <TabsList className="!flex flex-wrap justify-center gap-2 bg-transparent !h-auto !p-0" data-testid="menu-tabs">
            {categories.map(cat => (
              <TabsTrigger
                key={cat.key}
                value={cat.key}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === cat.key
                    ? 'text-white shadow-sm'
                    : 'text-[#1a1a1a] hover:bg-[#E5DCD3] bg-white border border-[#e0e0e0]'
                }`}
                style={activeTab === cat.key ? { backgroundColor: '#8B1E34' } : {}}
                data-testid={`tab-${cat.key}`}
              >
                {lang === 'fr' ? cat.name_fr : cat.name_en}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {/* Menu Grid */}
        {loading ? (
          <div className="text-center py-20 text-[#555555]">{t('common.loading')}</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="menu-grid">
            {filtered.map(item => (
              <MenuItemCard key={item.id} item={item} lang={lang} onAdd={addItem} modifiers={modifiers} ordering={ordering} />
            ))}
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <p className="text-center py-16 text-[#555555]">{lang === 'fr' ? 'Aucun plat dans cette catégorie' : 'No items in this category'}</p>
        )}
      </div>
    </div>
  );
}
